'use client';
import { ParsedUrlQuery } from 'querystring';

import React, { useEffect, useMemo, useState } from 'react';

import { useTheme } from 'styled-components';
import dynamic from 'next/dynamic';
import { useParams, useSearchParams } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';

import { FlexibleDiv } from '@components/formStyles';
import {
  CampaignBannerViewModel,
  ListingBannerViewModel,
  ListingNotFoundContentsViewModel,
  SearchViewModel,
  TourType,
  WebUrlType,
} from '@api/v3';
import SeoText from '@components/views/landing/components/seoText/seoText';
import HeroCampaignBanner from '@components/ui/banners/hero-campaign-banner/HeroCampaignBanner';
import { SORT_DATA } from '@constants/constants';
import { getCurrencySymbol } from '@utils/utils';
import Breadcrumb from '@components/Breadcrumb/Breadcrumb';
import { getLoyaltyTypeFromUtmSource } from '@components/ui/loyalty-verifications/helpers';
import { ListingMetaViewModel } from '@components/views/types';
import { getCurrentPageNumber, paramsToObject } from '@components/views/utils';
import ScrollToTop from '@components/ScrollToTop/ScrollToTop';
import { useSearchURL } from '@api/data-loaders/resource';
import useSearchBar from '@components/views/hooks/useSearchBar';
import usePagination from '@views/hooks/usePagination';
import { useDevice } from '@contexts/device';
import { getListingChatGPTMessage } from '@components/ChatGPTLoader/utils';
import {
  useHideChatGPT,
  useSetChatGPTMessage,
} from '@components/ChatGPTLoader/hooks';
import Typography from '@components/Typography';
import { TourListSchemaOrg } from '@utils/schemaorg/TourListSchemaOrg';
import {
  useGetFavoriteListItems,
  useGetFavoriteLists,
} from '@components/views/my-favorites/hooks';
import { useAuthState } from '@contexts/auth/AuthContext';
import { findDefaultList } from '@components/views/my-favorites/MyFavoriteComponent/utils';
import {
  tourListAndClick,
  shipListAndClick,
} from '@utils/newDataLayer/productListAndClick';
import GeneralLoader from '@components/ui/GeneralLoader';
import { useListingBannerFetching } from '@components/views/hotel/listing/hooks/useListingBannerFetching';
import useRouterNext from '@hooks/useRouterNext';
import { callService } from '@api/remote-call';
import { SessionScopeGTM } from '@utils/gtm/session-scope';

import { getDataProps } from '../../common/propsGetters';
import {
  Wrapper,
  ListWrapper,
  SEOWrapper,
  ListingCampaignBanner,
  SkeletonContainer,
  SearchBarWrapper,
  SepareteBottom,
  SelectedSegmentsContainer,
  ListingItemContainer,
  TourCardWrapper,
} from './TourListingStyle';
import TourCardSkeleton from '../components/ListingSkeleton/TourCardSkeleton';
import { useListingFetching } from './useListingFetching';
import {
  BreadcrumbWrapperListing,
  TourTitle,
  TourTitleMobile,
} from '../detail/styled';
import TourListingSearchBar from './TourListingSearchBar';
import NextTourListingCard from '../cards/next/TourListingCard';

const SelectedSegmentsMobile = dynamic(
  () => import('@components/views/hotel/components/SelectedSegmentsMobile')
);

const TourFilters = dynamic(() => import('./TourFilters'));
const SortingTabs = dynamic(
  () => import('@components/views/hotel/components/SortingTabs')
);
const LoyaltyController = dynamic(
  () => import('@components/ui/loyalty-verifications/LoyaltyController')
);
const Pagination = dynamic(
  () => import('@components/Pagination/next/Pagination')
);
const Banner = dynamic(
  () => import('@components/views/hotel/listing/components/Banner/Banner')
);

const SelectedSegments = dynamic(
  () => import('@components/views/hotel/components/SelectedSegments')
);

const NotFoundTourListing = dynamic(() => import('./NotFoundListing'));

interface TourListingProps {
  resource: 'Tur' | 'Gemi';
  metaInfo?: Record<string, string>;
  bannerInfo?: ListingBannerViewModel;
  campaignBanner?: CampaignBannerViewModel;
  initialData?: any;
  popularSearchData?: ListingNotFoundContentsViewModel;
  initialSearchesUrl: SearchViewModel;
}
const TourListing = ({
  resource,
  metaInfo,
  bannerInfo,
  campaignBanner,
  initialData,
  popularSearchData,
  initialSearchesUrl,
}: TourListingProps) => {
  const { palette } = useTheme();
  const { push } = useRouterNext();
  const { isMobile, isTablet, isDesktop } = useDevice();
  const [sort, setSort] = useState(SORT_DATA[0]);
  const chatGPTBannerCount = 10;

  const urlData = useSearchURL();

  const params = useParams();

  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);

  const { loginInfo } = useAuthState();
  const { favoriteLists, refetchFavoriteLists } = useGetFavoriteLists({
    token: loginInfo?.token ?? '',
  });

  const defaultList = useMemo(() => {
    return findDefaultList(favoriteLists);
  }, [favoriteLists]);

  const {
    favoriteListItems,
    refetchFavoriteListItems,
    isFavoriteListItemError,
  } = useGetFavoriteListItems({
    wishListId: defaultList?.id ?? '',
    size: 1000,
  });

  const {
    destination,
    onDestinationChange,
    onDestinationTextChange,
    destinationSearchText,
    errors,
    searchItem,
  } = useSearchBar(resource === 'Tur' ? 'tour' : 'ship', urlData?.text || '');

  const activeLocationText = useMemo(() => {
    if (
      searchItem?.type === WebUrlType.TOUR_CITY ||
      searchItem?.type === WebUrlType.TOUR_COUNTRY ||
      searchItem?.type === WebUrlType.TOUR_REGION ||
      searchItem?.type === WebUrlType.TAG ||
      searchItem?.type === WebUrlType.TOUR_CONTINENT
    ) {
      return `${searchItem?.text} Turları`;
    } else if (searchItem?.type === WebUrlType.CRUISE_REGION) {
      return `${searchItem.text} Gemi Turları`;
    } else {
      return searchItem?.text;
    }
  }, [searchItem]);

  const {
    isError,
    tours = [],
    filters,
    isFetching,
    meta = {} as ListingMetaViewModel,
    refetch,
    isListingSuccess,
  } = useListingFetching(initialData);

  const { bannerData } = useListingBannerFetching({
    initialData: bannerInfo,
  });

  const skeletonItems = useMemo(
    () =>
      Array(9)
        .fill(0)
        .map((_, i) => ({ id: `skeleton-${i}` })),
    []
  );
  const loyaltyType = getLoyaltyTypeFromUtmSource(query?.utm_source as string);

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setCurrentPage(query.page ? parseInt(query.page.toString()) : 1);
    }
  }, [query]);

  // ============================
  // Handle sortBy click
  // ============================
  const handleTabClick = (activeKey: string): void => {
    setIsRedirecting(true);
    const item = SORT_DATA.find((item) => item.id === activeKey);

    if (item) {
      setSort(item);

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { ...nextQuery } = query;

      push({
        pathname: `/${params?.slug}`,
        query: { ...nextQuery, sort: item.name },
      });
    }
  };

  // ============================
  // Handle set the chatGPT message
  // ============================

  useHideChatGPT(false);
  useSetChatGPTMessage(getListingChatGPTMessage('Tour'));

  // ============================
  // Handle Pagination change
  // ============================
  usePagination(currentPage);

  // ============================================
  // Fill the sortBy filed when the page load or query updated
  // =================================================
  useEffect(() => {
    const activeSort = SORT_DATA.find((item) => item.name === query.sort);

    if (!activeSort) return setSort(SORT_DATA[0]);
    setSort(activeSort);
  }, [query]);

  // ============================
  // Render the hero campaign
  // ============================
  const image = isDesktop
    ? campaignBanner?.images?.[0]?.url
    : campaignBanner?.responsiveImages?.[0]?.url;
  const renderHeroCampaign = () => (
    <ListingCampaignBanner>
      <HeroCampaignBanner
        listing={true}
        images={image}
        data={getDataProps(!!isDesktop, palette.eclipse, campaignBanner)}
        pageName={meta.title + ' Listing Page'}
      />
    </ListingCampaignBanner>
  );

  // ================================================
  // Generate breadcrumb and the banner images data
  // ================================================
  const breadItems = useMemo(() => {
    return [
      {
        title: 'Ana Sayfa',
        url: '/',
      },
      {
        title: activeLocationText,
        url: `/${params?.slug}`,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchItem?.text, query]);

  const bannerImages = useMemo(() => {
    if (!bannerData) return [];
    return bannerData.items?.map(({ images = [], url }, index) => ({
      url: images[0]?.url,
      alt: `${meta?.title} Kampanyaları ${index + 1}`,
      href: url,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bannerData]);

  // ====================================================
  // we should do shallow push when the user search for a tour listing
  // ====================================================
  const shouldShallowPush = useMemo(() => {
    return (
      destination?.type === WebUrlType.TOUR_CITY ||
      destination?.type === WebUrlType.TOUR_TYPE ||
      destination?.type === WebUrlType.TOUR_THEME ||
      destination?.type === WebUrlType.TOUR_REGION ||
      destination?.type === WebUrlType.TOUR_COUNTRY ||
      destination?.type === WebUrlType.TOUR_CATEGORY ||
      destination?.type === WebUrlType.CRUISE_THEME ||
      destination?.type === WebUrlType.CRUISE_REGION ||
      destination?.type === WebUrlType.CRUISE_PORT ||
      destination?.type === WebUrlType.CRUISE_CATEGORY
    );
  }, [destination]);

  const heroCampaignPosition = isMobile || isTablet ? 6 : 4;

  const showLoadMoreButton =
    !isError &&
    tours &&
    meta.totalPages > 1 &&
    tours.length < (meta && meta?.totalCount ? meta?.totalCount : 0);

  const metaContentControl = metaInfo?.content === '<p><br></p>';
  const isSegmentPage = initialSearchesUrl?.segment;

  const { data: segments } = useQuery(
    ['segments'],
    () =>
      callService({
        service: 'ToursService',
        method: 'segments',
        version: 'v3',
      }),
    {
      enabled: isSegmentPage,
    }
  );

  useEffect(() => {
    if (activeLocationText?.includes('Gemi')) {
      tours &&
        tours?.length > 0 &&
        tours[0] &&
        shipListAndClick(tours[0], sort, filters, query, false);
    } else {
      tours &&
        tours?.length > 0 &&
        tours[0] &&
        tourListAndClick(tours[0], sort, filters, query, false);
    }
  }, [tours]);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    if (isListingSuccess && !isFetching) {
      setIsRedirecting(false);
    }
  }, [isListingSuccess, isFetching]);

  const isAbroad = tours?.[0]?.[0]?.tourType === TourType.ABROAD;
  const isCruise = tours?.[0]?.[0]?.tourType === TourType.CRUISE;

  SessionScopeGTM(
    'Listeleme Sayfası',
    isCruise
      ? 'LP - Yurt Dışı Gemi'
      : isAbroad
      ? 'LP - Yurt Dışı Tur'
      : 'LP - Yurt İçi Tur'
  );
  const updateDateParamInUrl = (itemUrl?: string) => {
    if (itemUrl && itemUrl?.includes('date')) {
      const url = new URL(itemUrl ?? '', process.env.NEXT_PUBLIC_HOST);
      const dateParams = url.searchParams.getAll('date');
      url.searchParams.delete('date');
      url.searchParams.append('date', dateParams.join(','));

      return url.toString().replace(process.env.NEXT_PUBLIC_HOST ?? '', '');
    } else {
      return itemUrl;
    }
  };

  const shouldRenderHeroCampaign = (index: number) => {
    const isLastItemOfFirstPage =
      index === heroCampaignPosition - 1 && currentPage === 1;

    if (isDesktop) {
      return isLastItemOfFirstPage && campaignBanner?.images?.[0]?.url;
    }

    return (
      isLastItemOfFirstPage &&
      meta?.totalCount &&
      heroCampaignPosition < meta?.totalCount &&
      campaignBanner?.responsiveImages?.[0]?.url
    );
  };

  return isError || (initialData === null && popularSearchData) ? (
    <>
      <NotFoundTourListing
        tourListCount={meta?.totalCount || 0}
        type={resource === 'Tur' ? 'tour' : 'ship'}
        popularSearchData={popularSearchData}
      />
      {metaInfo?.content && !metaContentControl && currentPage == 1 ? (
        <SEOWrapper>
          <SeoText text={metaInfo?.content || ''} />
        </SEOWrapper>
      ) : null}
    </>
  ) : (
    <>
      <ListWrapper>
        <ScrollToTop />
        {tours && destination ? (
          <TourListSchemaOrg tour={tours} destination={meta} />
        ) : null}
        {isDesktop ? (
          <aside>
            <TourFilters
              isLoadingFilters={isFetching}
              count={meta?.totalCount}
              applicableFilters={filters}
              itemText={searchItem?.text ?? ''}
              setIsRedirecting={setIsRedirecting}
              isSegmentPage={isSegmentPage}
            />
          </aside>
        ) : null}
        <main>
          <Wrapper>
            {isDesktop && bannerImages.length > 0 ? (
              <FlexibleDiv top={20} bottom={20}>
                <Banner images={bannerImages} />
              </FlexibleDiv>
            ) : null}
            {isDesktop ? (
              <>
                {searchItem ? (
                  <TourTitle>{activeLocationText}</TourTitle>
                ) : null}
              </>
            ) : (
              <>
                {searchItem ? (
                  <TourTitleMobile>
                    <Typography variant='caption' weight='SemiBold' as='h1'>
                      {activeLocationText}
                    </Typography>
                  </TourTitleMobile>
                ) : null}
              </>
            )}
            <BreadcrumbWrapperListing>
              <Breadcrumb
                setIsRedirecting={setIsRedirecting}
                items={breadItems}
              />
            </BreadcrumbWrapperListing>
            <FlexibleDiv
              top={isDesktop ? 15 : 0}
              bottom={segments && isSegmentPage ? 10 : 20}
            >
              <SearchBarWrapper>
                <TourListingSearchBar
                  setIsRedirecting={setIsRedirecting}
                  tourListing={true}
                  tourListCount={meta?.totalCount || 0}
                  type={resource === 'Tur' ? 'tour' : 'ship'}
                  shouldShallowPush={shouldShallowPush}
                  destination={destination || searchItem}
                  activeDestinationText={searchItem?.text ?? ''}
                  onDestinationChange={onDestinationChange}
                  onDestinationTextChange={onDestinationTextChange}
                  destinationSearchText={destinationSearchText}
                  errors={errors}
                  isSegmentPage={isSegmentPage}
                />
              </SearchBarWrapper>
              {!isDesktop && segments && isSegmentPage ? (
                <SelectedSegmentsContainer>
                  <SelectedSegmentsMobile data={segments} />
                </SelectedSegmentsContainer>
              ) : null}
            </FlexibleDiv>
            <FlexibleDiv bottom={isDesktop ? 21 : -36}>
              {isDesktop && segments && isSegmentPage ? (
                <SelectedSegments data={segments} />
              ) : null}

              {isDesktop && !isError ? (
                <SortingTabs
                  select='tour'
                  activeKey={sort.id}
                  data={SORT_DATA}
                  handleTabClick={handleTabClick}
                />
              ) : null}

              {isFetching ||
              currentPage !== getCurrentPageNumber(query.page) ? (
                <SkeletonContainer>
                  {skeletonItems.map((item) => (
                    <TourCardSkeleton key={item.id} />
                  ))}
                </SkeletonContainer>
              ) : (
                <ListingItemContainer>
                  {tours[tours.length - 1]?.map((item, index: number) => (
                    <div key={item.id}>
                      <TourCardWrapper initialItem={Boolean(index === 0)}>
                        <NextTourListingCard
                          setIsRedirecting={setIsRedirecting}
                          id={item.id}
                          badgeAnimation={item.badges}
                          badge={item.features}
                          seturExecutive={item.seturExecutive}
                          tourListing={true}
                          installments={item.installments}
                          name={item.name}
                          image={item.image}
                          boardType={item.tourType}
                          nightCount={item.nightStay}
                          isTour={true}
                          discountRate={item.pricing?.rate}
                          discounted={item.pricing?.discounted}
                          originalPrice={item.pricing?.original}
                          sellingPrice={item.pricing?.selling}
                          discountType={item.pricing?.type}
                          rate={item.pricing?.rate}
                          currency={getCurrencySymbol(item.pricing?.currency)}
                          secure={item.secure}
                          departureCityName={item.departureCityName}
                          features={item.features}
                          placesToGo={item.placesToGo}
                          nightStay={item.nightStay}
                          dayStay={item.dayStay}
                          url={updateDateParamInUrl(item.url)}
                          transportionTypeName={item.transportationTypeName}
                          transportionTypeCode={
                            item.transportationTypeCode ?? ''
                          }
                          dates={item.dates}
                          images={item.images}
                          promotion={item?.promotions}
                          fallBackImage='/static/images/setur-static.jpg'
                          videos={item.videos ?? []}
                          gtmClick={() => {
                            if (activeLocationText?.includes('Gemi')) {
                              shipListAndClick(
                                [item],
                                sort,
                                filters,
                                query,
                                true
                              );
                            } else {
                              tourListAndClick(
                                [item],
                                sort,
                                filters,
                                query,
                                true
                              );
                            }
                          }}
                          favoriteLists={favoriteLists}
                          refetchFavoriteLists={refetchFavoriteLists}
                          defaultList={defaultList}
                          defaultListItems={
                            isFavoriteListItemError
                              ? []
                              : favoriteListItems?.data
                          }
                          refetchDefaultList={refetchFavoriteListItems}
                          segments={item.tags}
                          initialItem={Boolean(index === 0)}
                        />
                      </TourCardWrapper>

                      {shouldRenderHeroCampaign(index)
                        ? renderHeroCampaign()
                        : null}

                      {index === chatGPTBannerCount - 1 &&
                      currentPage === 1 &&
                      (isAbroad ||
                        params?.slug === 'yurtici-kultur-turlari') ? (
                        <div id='chat_gpt_insider' />
                      ) : null}
                    </div>
                  ))}
                </ListingItemContainer>
              )}
            </FlexibleDiv>
            {showLoadMoreButton ? (
              <Pagination
                totalItemCount={meta.totalCount}
                currentPage={meta.currentPage}
                totalPages={meta.totalPages}
                searchTitle={activeLocationText ?? meta.title?.split('|')[0]}
                type='tour'
                onPageChange={setCurrentPage}
              />
            ) : null}
          </Wrapper>
        </main>
        {isRedirecting ? <GeneralLoader /> : null}
      </ListWrapper>
      <SepareteBottom />
      {metaInfo?.content && !metaContentControl && currentPage == 1 ? (
        <SEOWrapper>
          <SeoText text={metaInfo?.content || ''} />
        </SEOWrapper>
      ) : null}
      {loyaltyType ? (
        <LoyaltyController loyaltyType={loyaltyType} refetch={refetch} />
      ) : null}
    </>
  );
};

export default TourListing;
